import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  NumericInputDS,
  PhotosInputDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { Switch } from "@qivia/ui/src/designSystem/components/Switch";
import { TextAreaDS } from "@qivia/ui/src/designSystem/components/Inputs/TextAreaInput";
import "../../../i18n/i18n";

interface VehicleConditionFormProps {
  mileage?: number | "";
  setMileage?: (value: number | "") => void;
  isCabinCleaned: boolean;
  setIsCabinCleaned: (value: boolean) => void;
  isTrunkCleaned: boolean;
  setIsTrunkCleaned: (value: boolean) => void;
  observations: string;
  setObservations: (value: string) => void;
  photos: File[];
  setPhotos: (files: File[]) => void;
  oldPhotos?: { uuid: string; url: string }[];
  onRemoveOldPhotos?: (uuid: string) => void;
  formError?: string;
}

export const VehicleConditionForm = (props: VehicleConditionFormProps) => {
  const { t } = useTranslation();

  return (
    <StyledForm>
      {props.formError && (
        <StyledErrorMessage>
          <Spacer y={1} />
          <TextCapitalized>{props.formError}</TextCapitalized>
        </StyledErrorMessage>
      )}
      <Spacer y={1} />
      {props.setMileage && (
        <NumericInputDS
          label={t("stateBeforeDeparture.mileage.label")}
          value={props.mileage ?? ""}
          update={props.setMileage}
          error={
            !!props.formError && props.mileage === 0
              ? t("mileage.error.input") || ""
              : undefined
          }
        />
      )}
      <StyledRow>
        <TextCapitalized>
          {t("restitution.vehicleCondition.isCabinCleaned")}
        </TextCapitalized>
        <Switch
          checked={props.isCabinCleaned}
          onClick={() => props.setIsCabinCleaned(!props.isCabinCleaned)}
          size="S"
          color="GREEN"
        />
      </StyledRow>
      <Spacer y={1} />
      <StyledRow>
        <TextCapitalized>
          {t("restitution.vehicleCondition.isTrunkCleaned")}
        </TextCapitalized>
        <Switch
          checked={props.isTrunkCleaned}
          onClick={() => props.setIsTrunkCleaned(!props.isTrunkCleaned)}
          size="S"
          color="GREEN"
        />
      </StyledRow>
      <Spacer y={1.5} />
      <StyledFlex>
        <TextCapitalized>
          {t("restitution.vehicleCondition.observations.label")}
        </TextCapitalized>
      </StyledFlex>
      <Spacer y={0.5} />
      <StyledTextArea>
        <TextAreaDS
          value={props.observations}
          $update={props.setObservations}
          placeholder={
            t("restitution.vehicleCondition.observations.placeholder") || ""
          }
        />
      </StyledTextArea>
      <Spacer y={1.5} />
      <StyledFlex>
        <TextCapitalized>
          {t("restitution.vehicleCondition.photos.label")}
        </TextCapitalized>
      </StyledFlex>
      <Spacer y={0.5} />
      <PhotosInputDS
        newPictures={{ photos: props.photos, setPhotos: props.setPhotos }}
        oldPictures={{
          photos: props.oldPhotos ?? [],
          onRemove: props.onRemoveOldPhotos,
        }}
        canAdd
        canRemove
        withViewIcon
      />
      <Spacer y={1.5} />
    </StyledForm>
  );
};

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledTextArea = styled.div`
  width: 100%;
  & textarea {
    box-sizing: border-box;
  }
`;
